<script setup lang="ts">
import { ChevronRight } from 'lucide-vue-next'
import type { ToolActivity } from '~/types/web-chat'
import type { ToolActivityKind } from '~~/shared/web-chat/toolActivity'
import ToolActivityChip from '~/components/ToolActivityChip.vue'

const props = defineProps<{
  activities: ToolActivity[]
}>()

const { t } = useI18n()

/**
 * Genuine repeats — the SAME tool called back-to-back with the SAME query — collapse into one chip
 * with a "×N" mark, rather than listing an identical line twice. Matched on `kind` + the first query
 * only: two DIFFERENT tools that happen to share wording (e.g. a workflow search and a legal search
 * both run with "contrato de trabajo") are never merged — that would hide that two different systems
 * were actually consulted, which is real information, not noise.
 */
const mergedActivities = computed<ToolActivity[]>(() => {
  const merged: ToolActivity[] = []
  for (const activity of props.activities) {
    const prev = merged[merged.length - 1]
    const sameTask = prev
      && (prev.kind ?? 'generic') === (activity.kind ?? 'generic')
      && (prev.kind ?? 'generic') !== 'generic' // an unclassified tool has nothing reliable to match on
      && (prev.queries?.[0] ?? null) === (activity.queries?.[0] ?? null)
      && prev.queries?.[0] != null
    if (sameTask && prev) {
      prev.count = (prev.count ?? 1) + 1
      prev.completed = prev.completed && activity.completed
      prev.queries = [...(prev.queries ?? []), ...(activity.queries ?? [])]
      prev.details = [...(prev.details ?? []), ...(activity.details ?? [])]
    } else {
      merged.push({ ...activity })
    }
  }
  return merged
})

// A4: a run often produces several "Buscando en la legislación…" activities. Rather than a long flat
// stack, collapse them under one macro disclosure once there's more than one. The in-progress
// activities always stay visible; completed ones tuck behind the summary until the user expands it.
const expanded = ref(false)
const collapsible = computed(() => mergedActivities.value.length > 1)

// Search-like when every activity carries queries — lets the summary read "N búsquedas…" instead of a
// generic step count.
const searchLike = computed(() => mergedActivities.value.every(a => (a.queries?.length ?? 0) > 0))

// When every activity in the group shares one domain, the summary can honestly name it ("N búsquedas
// de plantillas"). A MIXED batch (e.g. a legal search next to a workflow search) falls back to the
// domain-neutral wording — they are still all searches, just not attributable to one single domain.
const SEARCH_SUMMARY_KEY: Record<ToolActivityKind, string> = {
  legal: 'webChat.activitySearchSummary',
  workflow: 'webChat.activitySearchSummaryWorkflow',
  documents: 'webChat.activitySearchSummaryDocuments',
  help: 'webChat.activitySearchSummaryHelp',
  generic: 'webChat.activitySearchSummaryGeneric',
}
const commonKind = computed<ToolActivityKind | null>(() => {
  const kinds = new Set(mergedActivities.value.map(a => a.kind ?? 'generic'))
  return kinds.size === 1 ? [...kinds][0]! : null
})
const summaryLabel = computed(() => {
  if (!searchLike.value) return t('webChat.activityStepsSummary', mergedActivities.value.length)
  return t(SEARCH_SUMMARY_KEY[commonKind.value ?? 'generic'], mergedActivities.value.length)
})

const visibleActivities = computed(() =>
  !collapsible.value || expanded.value
    ? mergedActivities.value
    : mergedActivities.value.filter(a => !a.completed),
)
</script>

<template>
  <div v-if="activities.length > 0" class="flex flex-col gap-1 items-start">
    <button
      v-if="collapsible"
      type="button"
      class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <ChevronRight class="h-3.5 w-3.5 transition-transform" :class="expanded && 'rotate-90'" />
      {{ summaryLabel }}
    </button>
    <ToolActivityChip
      v-for="activity in visibleActivities"
      :key="activity.id"
      :activity="activity"
    />
  </div>
</template>
